import { batch } from "solid-js";
import { reconcile } from "solid-js/store";

import { Delete, Get, Post } from "@repo/utils/api";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import Cart from "~/components/checkout/Cart";

import { api } from "..";

import { Booking } from "./bookings";
import { Customer, useCustomers } from "./customers";
import { Organization } from "./organizations";

export type Order = {
  purchasableId: Cart.Item.Id;
  resourceId: Booking.Resource.Id | PaymentTerminal.Id;
  guestDetails?: Booking.Guest;
};

export type PaymentTerminal = {
  id: PaymentTerminal.Id;
  name: string;
};

export namespace PaymentTerminal {
  export type Id = `st_${string}`;
  export namespace Id {
    export const is = (id: string | undefined | null): id is Id =>
      !!id?.startsWith("st_");
  }
}

export type TerminalOrder = {
  id: TerminalOrder.Id;
  status: TerminalOrder.Status;
};

export namespace TerminalOrder {
  export type Id = `to_${string}`;
  export type Status = "pending" | "completed" | "failed" | "cancelled";
}

export type PointOfSaleRest = Post<
  { orders: Order[]; customerId?: Customer.Id },
  { terminalOrder?: TerminalOrder; customer?: Customer }
>;

export type TerminalsRest = Get<{}, { terminals: PaymentTerminal[] }>;

export type TerminalOrdersRest = {
  [id: TerminalOrder.Id]: Get<{}, { terminalOrder: TerminalOrder }> &
    Delete<{}, {}>;
};

const initialState = {
  terminalById: {} as Record<PaymentTerminal.Id, PaymentTerminal>,
  terminalsByOrgId: {} as Record<Organization.Id, PaymentTerminal[]>,
  terminalOrderById: {} as Record<TerminalOrder.Id, TerminalOrder>,
};

export const { Context, Provider, usePointOfSale } = FeatureStore.init(
  "PointOfSale",
  initialState,
  ([store, setStore], { attachAction, wrapActions }) => {
    const [, setCustomers] = useCustomers();

    const accessors = {
      terminalById: (id: PaymentTerminal.Id | undefined) =>
        id && store.terminalById[id],
      terminalOrderById: attachAction(
        (id: TerminalOrder.Id) => store.terminalOrderById[id],
        (id) => actions.fetchTerminalOrder(id),
      ),
      forOrganization: {
        terminals: attachAction(
          (orgId: Organization.Id) => store.terminalsByOrgId[orgId],
          (orgId) => actions.forOrganization.fetchTerminals(orgId),
        ),
      },
    };
    const mutations = {
      oneTerminal(terminal: PaymentTerminal) {
        setStore("terminalById", terminal.id, reconcile(terminal));
      },
      manyTerminals(terminals: PaymentTerminal[]) {
        batch(() => {
          for (const terminal of terminals) {
            mutations.oneTerminal(terminal);
          }
        });
      },
      oneTerminalOrder(terminalOrder: TerminalOrder) {
        setStore(
          "terminalOrderById",
          terminalOrder.id,
          reconcile(terminalOrder),
        );
      },
    };
    const actions = wrapActions({
      checkout(
        orgId: Organization.Id,
        orders: Order[],
        customerId?: Customer.Id,
      ) {
        return api.organizations[orgId]!.pos.POST({
          orders,
          customerId,
        }).then(({ terminalOrder, customer }) => {
          if (terminalOrder) mutations.oneTerminalOrder(terminalOrder);
          if (customer) setCustomers.one(customer);
          return terminalOrder;
        });
      },
      fetchTerminalOrder(terminalOrderId: TerminalOrder.Id) {
        return api["terminal-orders"]
          [terminalOrderId]!.GET({})
          .then(({ terminalOrder }) => {
            mutations.oneTerminalOrder(terminalOrder);
            return terminalOrder;
          });
      },
      cancelTerminalOrder(terminalOrderId: TerminalOrder.Id) {
        return api["terminal-orders"][terminalOrderId]!.DELETE({}).then(() => {
          setStore("terminalOrderById", terminalOrderId, "status", "cancelled");
        });
      },
      forOrganization: {
        fetchTerminals(orgId: Organization.Id) {
          return api.organizations[orgId]!.terminals.GET({}).then(
            ({ terminals }) => {
              batch(() => {
                mutations.manyTerminals(terminals);
                setStore("terminalsByOrgId", orgId, terminals);
              });
            },
          );
        },
      },
    });
    return { accessors, actions };
  },
);
